import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  isRudderstackReauthRequired,
  RUDDERSTACK_REAUTH_ACTION,
  RUDDERSTACK_REAUTH_MESSAGE,
} from '../lib/rudderstack-reauth';
import { useRudderstackReauth } from './useRudderstackReauth';

/**
 * "Sign in again" for an action that failed because the person's RudderStack sign-in ended (ACT2-1124, F12). It keeps
 * the person on this page: with the shared session, Lookout's session first renews from it, and the person tries the
 * action again; otherwise the person signs out and signs in, and comes back to this page.
 */
export function useRudderstackSignInAgain(): ReturnType<typeof useRudderstackReauth> & {
  /** For an `onError`: shows the sign-in toast and returns true for an ended sign-in, else returns false. */
  toastIfReauth: (err: unknown) => boolean;
} {
  const { pathname, search } = useLocation();
  const reauth = useRudderstackReauth({
    returnTo: `${pathname}${search}`,
    onRenewed: () => toast.success('You are signed in again. Try that once more.'),
  });
  // The toast's button runs after this render, so it reads the latest `start`.
  const startRef = useRef(reauth.start);
  useEffect(() => {
    startRef.current = reauth.start;
  });
  const toastIfReauth = useCallback((err: unknown): boolean => {
    if (!isRudderstackReauthRequired(err)) return false;
    toast.error(RUDDERSTACK_REAUTH_MESSAGE, {
      action: { label: RUDDERSTACK_REAUTH_ACTION, onClick: () => startRef.current() },
    });
    return true;
  }, []);
  return { ...reauth, toastIfReauth };
}
